<script setup>
/**
 * Every message the owner's own systems asked us to send — newest first.
 *
 * ── NEWEST FIRST IS A SERVER DECISION, AND IT NEEDED A NEW ONE ────────────────────────
 * The only other account-scoped read of this table is ascending by `updated_at` and is the
 * PUBLIC API's durable sweep cursor: an integrator polls it to catch up on state changes,
 * and repurposing it for a screen would have made a UI pager and somebody's reconciliation
 * job share an ordering neither could change. So the sidecar grew `order=desc` with an
 * offset pager of its own, the BFF pins `desc` for this route, and this page never asks.
 *
 * ── THE EMPTY STATE IS TWO STATES AND THEY NEED OPPOSITE ANSWERS ──────────────────────
 * "You have not sent anything yet" and "nothing you have sent is in this state" are
 * opposite facts about the same blank list, and an owner acts on them differently — one
 * sends her to Setup, the other to the filter she forgot she set. Telling them apart takes
 * a fact this page does not automatically have, because a filtered read cannot see past
 * its own filter. So the unfiltered total is LEARNED: it is free whenever the filter is
 * "All" (that response's `total` IS it), and when someone lands directly on a filtered URL
 * with nothing to show it is asked for once, with a one-row read. If that read fails, the
 * page drops the sentence that would have claimed there are messages rather than guessing
 * which of the two it is — see `EMPTY.FILTERED_BODY`, which asserts something.
 *
 * ── AND IT SAYS HOW LONG ROWS LIVE ────────────────────────────────────────────────────
 * Rows are pruned at the workspace's retention window, so an owner looking for last
 * quarter's send finds nothing and concludes her history vanished. The number is the
 * workspace's own (`settings.limits.retentionDays`), never a constant typed twice — and
 * that read is allowed to fail on its own: it degrades the sentence to "older ones are
 * removed after a while" and does not take the log down with it.
 *
 * ── THE FILTER AND THE PAGE ARE IN THE URL ────────────────────────────────────────────
 * `…/waslo/outbound/log?status=failed&offset=50`, the same reason Activity puts its period
 * there: "here are my failures" is a link an owner sends to whoever asked her the
 * question, and coming back from a message returns to the page she opened it from rather
 * than to the top of the list. `replace`, not `push`, for both — a filter is a view of one
 * screen, and pushing it would make Back walk through every status she tried instead of
 * leaving the section.
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import OutboundMessageRow from '../../components/outbound/OutboundMessageRow.vue';
import OutboundNotSetUp from '../../components/outbound/OutboundNotSetUp.vue';
import WasloExplainer from '../../components/WasloExplainer.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import WasloSelect from '../../components/WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { toIntlLocale } from '../../helpers/intl.js';
import {
  WASLO_ERROR_CODES,
  WASLO_OUTBOUND_STATUSES,
  wasloApi,
} from '../../api/client.js';

const { t, locale } = useWasloI18n();

const accountId = useWasloAccountId();
const route = useRoute();
const router = useRouter();

/** The BFF's own default page size. A third number here would be a third answer. */
const PAGE_SIZE = 50;

// ── what the URL is asking for ─────────────────────────────────────────────────────
// A URL is user input. Anything that is not one of the nine statuses is "All", and
// anything that is not a whole number of rows is the first page — never a 400.

const status = computed(() => {
  const raw = route?.query?.status;
  return WASLO_OUTBOUND_STATUSES.includes(raw) ? raw : '';
});

const offset = computed(() => {
  const raw = Number(route?.query?.offset);
  return Number.isInteger(raw) && raw > 0 ? raw : 0;
});

const setQuery = next => {
  const query = { ...(route?.query ?? {}) };
  Object.entries(next).forEach(([key, value]) => {
    if (value === '' || value === 0) delete query[key];
    else query[key] = String(value);
  });
  router.replace({ query });
};

// A new filter is a new list, so it starts at its own beginning: keeping the offset would
// land an owner on "showing 151–200 of 3" and an empty page she did not ask for.
const selectStatus = value => setQuery({ status: value, offset: 0 });

// ── the reads ──────────────────────────────────────────────────────────────────────

const messages = useWasloApi(signal =>
  wasloApi.getOutboundMessages(
    accountId.value,
    { status: status.value, limit: PAGE_SIZE, offset: offset.value },
    signal
  )
);

// `isEmpty: () => false` — the limits are a real answer whatever they say, and a settings
// read that came back is never this page's EMPTY state.
const settings = useWasloApi(
  signal => wasloApi.getOutboundSettings(accountId.value, signal),
  { isEmpty: () => false }
);

// Both are read inside the loader, so re-running it is the whole of "change the filter" and
// "turn the page". `useWasloApi` aborts the in-flight request first, so clicking through
// three filters quickly cannot land the first answer on top of the third.
watch([status, offset], () => messages.reload());

const page = computed(() => messages.data.value);
const rows = computed(() => page.value?.messages ?? []);
const total = computed(() => page.value?.total ?? 0);

/**
 * The 403 the sidecar answers when no WhatsApp number is wired for sending here.
 *
 * Not a failure, not something an owner can fix from this page, and emphatically not the
 * generic "check your connection" — so it outranks the error branch and renders as a
 * state of its own, with no ruby triangle and no Try again.
 *
 * KEYED ON THE BFF'S OWN CODE, not on `FORBIDDEN`. It was the latter, and that only ever
 * worked because every route in this section happens to be admin-only — true today and
 * not a contract. A genuine permission refusal now reads as one.
 */
const isNotSetUp = computed(
  () =>
    messages.error.value?.code === WASLO_ERROR_CODES.OUTBOUND_NOT_PROVISIONED
);

const errorMessage = computed(() =>
  isNotSetUp.value ? '' : messages.errorMessage.value
);

const isEmpty = computed(
  () =>
    isNotSetUp.value || (messages.hasLoaded.value && rows.value.length === 0)
);

// ── has this workspace EVER sent anything? ─────────────────────────────────────────
// Null means "we have not been able to find out", which is a third answer and is rendered
// as one.
const totalWithoutFilter = ref(null);

watch(page, value => {
  if (value && status.value === '') totalWithoutFilter.value = value.total;
});

const probing = ref(false);

watch([isEmpty, status], async ([empty, current]) => {
  if (
    !empty ||
    current === '' ||
    probing.value ||
    totalWithoutFilter.value !== null ||
    accountId.value === null
  ) {
    return;
  }
  probing.value = true;
  try {
    const probe = await wasloApi.getOutboundMessages(accountId.value, {
      limit: 1,
    });
    totalWithoutFilter.value = probe.total;
  } catch {
    // Left null on purpose. The empty state drops its claim rather than inventing one.
  } finally {
    probing.value = false;
  }
});

/** Which of the two blank lists this is — plus the third that is not blank at all. */
const emptyKind = computed(() => {
  if (isNotSetUp.value) return 'not-set-up';
  if (status.value === '' || totalWithoutFilter.value === 0) return 'nothing';
  return 'filtered';
});

// `not-set-up` is deliberately NOT in here: it is rendered by `OutboundNotSetUp`, the one
// component all three outbound screens share, so the three cannot tell one workspace three
// different stories about the same missing number.
const EMPTY_COPY = {
  nothing: {
    icon: 'i-lucide-send',
    title: 'WASLO.OUTBOUND.LOG.EMPTY.TITLE',
    body: 'WASLO.OUTBOUND.LOG.EMPTY.BODY',
  },
  filtered: {
    icon: 'i-lucide-filter',
    title: 'WASLO.OUTBOUND.LOG.EMPTY.FILTERED_TITLE',
    // Claimed only when the unfiltered read actually said so — see the header comment.
    body: 'WASLO.OUTBOUND.LOG.EMPTY.FILTERED_BODY',
  },
};

const empty = computed(() => {
  const copy = EMPTY_COPY[emptyKind.value];
  // `not-set-up` has no entry and gets none: the shared component owns that whole state.
  if (!copy) return null;
  const claimsMessagesExist = emptyKind.value === 'filtered';
  return {
    icon: copy.icon,
    title: t(copy.title),
    body: claimsMessagesExist && !totalWithoutFilter.value ? '' : t(copy.body),
  };
});

// ── the sentences ──────────────────────────────────────────────────────────────────
const intlLocale = computed(() => toIntlLocale(locale.value));

const formatCount = value =>
  new Intl.NumberFormat(intlLocale.value).format(value);

const statusOptions = computed(() => [
  { value: '', label: t('WASLO.OUTBOUND.STATUS.ALL') },
  ...WASLO_OUTBOUND_STATUSES.map(value => ({
    value,
    label: t(`WASLO.OUTBOUND.STATUS.${value.toUpperCase()}`),
  })),
]);

/** Nothing at all while the limits are still on their way — a "we could not read how long
 *  messages are kept" that turns into a number a moment later is worse than a beat of
 *  silence. */
const retention = computed(() => {
  if (settings.isFetching.value) return '';
  const days = settings.data.value?.limits?.retentionDays;
  return typeof days === 'number'
    ? t('WASLO.OUTBOUND.LOG.RETENTION', { days: formatCount(days) })
    : t('WASLO.OUTBOUND.LOG.RETENTION_UNKNOWN');
});

const showing = computed(() =>
  rows.value.length === 0
    ? ''
    : t('WASLO.OUTBOUND.COMMON.PAGER.SHOWING', {
        from: formatCount(offset.value + 1),
        to: formatCount(offset.value + rows.value.length),
        total: formatCount(total.value),
      })
);

const hasPrevious = computed(() => offset.value > 0);
const hasNext = computed(() => offset.value + rows.value.length < total.value);
const hasPager = computed(() => hasPrevious.value || hasNext.value);

const goPrevious = () =>
  setQuery({ offset: Math.max(0, offset.value - PAGE_SIZE) });
const goNext = () => setQuery({ offset: offset.value + PAGE_SIZE });
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.OUTBOUND.LOG.TITLE')"
    :is-fetching="messages.isFetching.value"
    :not-provisioned="messages.isNotProvisioned.value"
    :error-message="errorMessage"
    :is-empty="isEmpty"
    @retry="messages.reload()"
  >
    <!-- Outside the state branches on purpose: the filter stays put while the list below
         it reloads, and it is the one control an owner needs in the blank state. -->
    <template #controls>
      <div
        class="flex flex-wrap items-end gap-3 pb-4"
        data-waslo-outbound-filter
      >
        <div class="w-full sm:w-56">
          <WasloSelect
            :model-value="status"
            :label="t('WASLO.OUTBOUND.LOG.FILTER.LABEL')"
            :options="statusOptions"
            @update:model-value="selectStatus"
          />
        </div>
        <Button
          :label="t('WASLO.OUTBOUND.COMMON.REFRESH')"
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="messages.isLoading.value"
          :disabled="messages.isLoading.value"
          data-waslo-outbound-refresh
          @click="messages.reload()"
        />
      </div>
    </template>

    <template #emptyState>
      <!-- Not a blank list at all: no number is connected, so there is nothing this page
           could have listed. The same component says so on Setup and on one message. -->
      <OutboundNotSetUp v-if="emptyKind === 'not-set-up'" />

      <div
        v-else
        class="flex flex-col items-center justify-center gap-3 py-16 text-center"
        :data-waslo-outbound-empty="emptyKind"
      >
        <span class="size-6 text-n-slate-10" :class="empty.icon" />
        <span class="text-base font-medium text-n-slate-12">
          {{ empty.title }}
        </span>
        <p v-if="empty.body" class="max-w-md text-sm text-n-slate-11">
          {{ empty.body }}
        </p>

        <Button
          v-if="emptyKind === 'filtered'"
          :label="t('WASLO.OUTBOUND.LOG.EMPTY.CLEAR_FILTER')"
          variant="outline"
          color="slate"
          size="sm"
          data-waslo-outbound-clear-filter
          @click="selectStatus('')"
        />
        <p
          v-if="retention"
          class="max-w-md mb-0 text-sm text-n-slate-10"
          data-waslo-outbound-retention
        >
          {{ retention }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <WasloExplainer
          icon="i-lucide-send"
          :note="t('WASLO.OUTBOUND.LOG.SPOTLIGHT')"
        />

        <section class="flex flex-col gap-4" data-waslo-outbound-log>
          <SettingsHeader :heading="t('WASLO.OUTBOUND.LOG.HEADING')" />

          <ul
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
          >
            <OutboundMessageRow
              v-for="message in rows"
              :key="message.id"
              :message="message"
              :account-id="accountId"
              :server-time="page?.serverTime ?? ''"
            />
          </ul>

          <div class="flex flex-wrap items-center gap-3">
            <p
              v-if="showing"
              class="mb-0 text-sm text-n-slate-11"
              data-waslo-outbound-showing
            >
              {{ showing }}
            </p>
            <!-- NO CHEVRONS. "Previous" and "Next" are directions in reading order, and
                 a chevron is a direction on screen: the only icon that could sit on these
                 two would point the wrong way in Arabic, which is half this product's
                 audience. The words already say it in the reader's own order. -->
            <div
              v-if="hasPager"
              class="flex items-center gap-2 ms-0 sm:ms-auto"
            >
              <Button
                :label="t('WASLO.OUTBOUND.COMMON.PAGER.PREVIOUS')"
                variant="outline"
                color="slate"
                size="sm"
                :disabled="!hasPrevious"
                data-waslo-outbound-previous
                @click="goPrevious"
              />
              <Button
                :label="t('WASLO.OUTBOUND.COMMON.PAGER.NEXT')"
                variant="outline"
                color="slate"
                size="sm"
                :disabled="!hasNext"
                data-waslo-outbound-next
                @click="goNext"
              />
            </div>
          </div>

          <p
            v-if="retention"
            class="mb-0 text-sm text-n-slate-10"
            data-waslo-outbound-retention
          >
            {{ retention }}
          </p>
        </section>
      </div>
    </template>
  </WasloPageLayout>
</template>
